import { getFontCatalog } from "../fontCatalog.js";
import { loadLocalModule } from "../nodeLoader.js";
import { getCachedScript } from "../scriptCache.js";
import {
  buildFontLinks,
  generateFontText,
  getKnownFontFamilies,
  makeFontResultMessage,
} from "../linksFonts.js";

const getProjectPath = () =>
  new Promise((resolve, reject) => {
    const code = getCachedScript();

    if (!code || !window?.jsx?.evalScript) {
      reject(new Error("Premiere Pro scripting is not available"));
      return;
    }

    const script = `
      try {
        if (app && app.project && app.project.path) {
          app.project.path;
        } else {
          "__AE2_EMPTY_PROJECT_PATH__";
        }
      } catch (error) {
        "__AE2_PROJECT_PATH_ERROR__" + error.toString();
      }
    `;

    window.jsx.evalScript(script, (result) => {
      if (!result || result === "__AE2_EMPTY_PROJECT_PATH__") {
        reject(new Error("Save the Premiere Pro project before collecting fonts"));
        return;
      }

      if (result.indexOf("__AE2_PROJECT_PATH_ERROR__") === 0) {
        reject(
          new Error(result.replace("__AE2_PROJECT_PATH_ERROR__", "").trim()),
        );
        return;
      }

      resolve(result);
    });
  });

const saveProject = () =>
  new Promise((resolve, reject) => {
    const code = getCachedScript();

    if (!code || !window?.jsx?.evalScript) {
      reject(new Error("Premiere Pro scripting is not available"));
      return;
    }

    const script = `
      try {
        app.project.save();
      } catch (error) {
        "__AE2_SAVE_ERROR__" + error.toString();
      }
    `;

    window.jsx.evalScript(script, (result) => {
      if (
        result === "true" ||
        result === "0" ||
        result === true ||
        result === 0
      ) {
        resolve();
        return;
      }

      reject(
        new Error(
          (result || "__AE2_SAVE_ERROR__Project save failed")
            .replace("__AE2_SAVE_ERROR__", "")
            .trim(),
        ),
      );
    });
  });

const selectOutputFolder = () =>
  new Promise((resolve, reject) => {
    const script = `
      try {
        var selectedFolder = Folder.selectDialog("Select a folder for Fonts.txt");
        selectedFolder ? selectedFolder.fsName : "__AE2_FOLDER_CANCELLED__";
      } catch (error) {
        "__AE2_FOLDER_ERROR__" + error.toString();
      }
    `;

    window.jsx.evalScript(script, (result) => {
      if (result === "__AE2_FOLDER_CANCELLED__") {
        resolve(null);
        return;
      }
      if (!result || result.indexOf("__AE2_FOLDER_ERROR__") === 0) {
        reject(
          new Error(
            String(result || "Folder selection failed")
              .replace("__AE2_FOLDER_ERROR__", "")
              .trim(),
          ),
        );
        return;
      }
      resolve(result);
    });
  });

const getPremiereFontPaths = async (templateFolder) => {
  const projectPath = await getProjectPath();
  await saveProject();

  if (!templateFolder) {
    const outputPath = await selectOutputFolder();
    return outputPath ? { projectPath, outputPath } : { cancelled: true };
  }

  const nodeRequire = window.require || window.parent?.require;
  if (!nodeRequire) throw new Error("Node.js environment not detected");
  const nodePath = nodeRequire("path");

  return {
    projectPath,
    outputPath: nodePath.join(
      nodePath.dirname(projectPath),
      "Premiere Pro Template",
      "Fonts",
    ),
  };
};

const joinOutputFile = (folderPath) =>
  `${String(folderPath || "").replace(/[\\/]+$/, "")}/Fonts.txt`;

const showFontMessage = (message) => {
  window.jsx.evalScript(
    `alert(${JSON.stringify(message)});`,
    () => {},
  );
};

export default async function getFonts(site, templateFolder) {
  const paths = await getPremiereFontPaths(templateFolder);

  if (paths.cancelled) return { cancelled: true };

  const catalog = await getFontCatalog(site);
  const collector = loadLocalModule("premiereFontCollector");

  if (!collector || typeof collector.collectProjectFontsJson !== "function") {
    throw new Error("Premiere font collector could not be loaded");
  }

  const fontData = JSON.parse(
    collector.collectProjectFontsJson({
      projectPath: paths.projectPath,
      outputPath: paths.outputPath,
      knownFamilies: getKnownFontFamilies(catalog),
    }),
  );
  const fontLinks = buildFontLinks(fontData.fonts, catalog, site);
  const filePath = joinOutputFile(fontData.path);

  collector.writeTextFile(filePath, generateFontText(fontLinks));
  const message = makeFontResultMessage(fontLinks);
  showFontMessage(message);

  return {
    ...fontData,
    filePath,
    fontLinks,
    message,
  };
}
